import { useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * useWishlistLoader
 *
 * Cuando el usuario abre la pestaña "wishlu", carga automáticamente
 * su propia wishlist para mostrarla sin que tenga que buscarla.
 *
 * Respeta dos casos especiales:
 *   - Si ya está viendo su propia wishlist → no hace nada (evita re-cargar)
 *   - Si está viendo la wishlist de otra persona → tampoco interfiere
 *
 * Recuerda la última wishlist que usó guardándola en localStorage.
 *
 * Uso en App.js:
 *   useWishlistLoader({ user, userProfile, activeTab, viewingMember,
 *                       viewingWishlist, screen, setViewingMember,
 *                       setViewingWishlist, setScreen });
 */
export function useWishlistLoader({
  user,
  userProfile,
  activeTab,
  viewingMember,
  viewingWishlist,
  screen,
  setViewingMember,
  setViewingWishlist,
  setScreen,
}) {
  useEffect(() => {
    // Solo actuamos si hay usuario autenticado y está en la pestaña wishlu
    if (!user || !userProfile || activeTab !== "wishlu") return;

    // Si ya estamos viendo nuestra propia wishlist, no hacemos nada
    if (
      viewingWishlist &&
      screen === "profile" &&
      viewingMember?.id === user.uid
    )
      return;

    // Si estamos viendo la wishlist de otra persona, tampoco tocamos nada
    if (viewingMember && viewingMember.id !== user.uid) return;

    const todasLasWishlists = userProfile?.wishlists || [];
    if (todasLasWishlists.length === 0) return;

    // Intentamos cargar la última wishlist que usó el usuario
    let ultimaId = null;
    try {
      ultimaId = localStorage.getItem("whislu_last_wishlist");
    } catch (e) {}

    // Si la última usada sigue existiendo la usamos; si no, la primera disponible
    const idACargar =
      ultimaId && todasLasWishlists.includes(ultimaId)
        ? ultimaId
        : todasLasWishlists[0];

    if (!idACargar) return;

    async function cargarMiWishlist() {
      const wSnap = await getDoc(doc(db, "wishlists", idACargar));
      if (wSnap.exists()) {
        // Mostramos el perfil del propio usuario con su wishlist
        setViewingMember({ id: user.uid, ...userProfile });
        setViewingWishlist({ id: wSnap.id, ...wSnap.data() });
        setScreen("profile");
      }
    }

    cargarMiWishlist();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, activeTab]); // Solo re-ejecuta si cambia el usuario o la pestaña
}
